import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, WarningOctagon } from '@phosphor-icons/react'
import { Banner, Button, PageHeader, SkeletonText } from '@/ui'
import { errorMessage } from '../api/core'

/** Console page: header and a vertical stack of content. */
export function ConsolePage({
  eyebrow,
  title,
  description,
  actions,
  back,
  children,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** Back link (detail pages). */
  back?: { to: string; label: string }
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-5">
      {back && (
        <Link to={back.to} className="-mb-2 inline-flex w-fit items-center gap-1.5 text-[13px] text-muted hover:text-ink">
          <ArrowLeft aria-hidden="true" /> {back.label}
        </Link>
      )}
      <PageHeader eyebrow={eyebrow} title={title} description={description} actions={actions} />
      {children}
    </div>
  )
}

/** Main + side columns for detail pages; stacks below lg (tablet). */
export function DetailColumns({ main, side }: { main: ReactNode; side: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex min-w-0 flex-col gap-5">{main}</div>
      <aside className="flex min-w-0 flex-col gap-5">{side}</aside>
    </div>
  )
}

/** Loading / error / content for one query on a detail page. */
export function QueryState({
  query,
  children,
  label = 'this',
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown }
  children: ReactNode
  label?: string
}) {
  if (query.isLoading)
    return (
      <div className="rounded-card border border-rule bg-surface p-5" aria-busy="true">
        <SkeletonText lines={5} />
      </div>
    )
  if (query.isError)
    return (
      <Banner
        tone="error"
        title={`Could not load ${label}`}
        action={
          <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
            Try again
          </Button>
        }
      >
        <span className="inline-flex items-center gap-1">
          <WarningOctagon aria-hidden="true" className="sr-only" />
          {errorMessage(query.error)}
        </span>
      </Banner>
    )
  return <>{children}</>
}
